import type {
  AcademicTerm,
  AssignmentCopyRequest,
  AssignmentFolderAction,
  ConflictV2,
  DailyMemoAction,
  DailyRecordAction,
  DrawAction,
  DrawState,
  HomeroomAffairChange,
  OperationV2,
  SyncClock,
} from "@classroom/domain";
import type { ClassSetupDraft } from "../classSetup";
import { syncReviewId } from "../driveSync/reviewScope";
import { DRIVE_SYNC_AVAILABLE } from "../features";
import {
  accountEpoch,
  assertAccountSession,
  assertOperationScope,
  beginAccountSession,
  type LocalAccount,
} from "./accountScope";
import { hasPendingBackupJobs } from "./backupJob";
import type { CloudImportPreview } from "./cloudImport";
import type { LocalOperation, LocalRequest, LocalResponse } from "./protocol";
import { localOperationAccess } from "./protocol";
import type { ClassSettingsUpdate, LocalBackupSource, LocalSnapshot, StoredRecord } from "./repository";
import type { Collection } from "./schema";
import type { LocalSyncState, SyncAttachment, SyncReceiveResult } from "./syncV2Store";

const clients = new Set<LocalStoreClient>();
// Session-only keys connect the console and controller Workers in this tab.
// CryptoKey handles never enter localStorage; a shared device loses them when
// the tab closes. Trusted-device persistence is an explicit DeviceVault choice.
const syncSessions = new Map<string, { scope: SyncAttachment; key: CryptoKey }>();

export function hasPendingLocalRequests() {
  return hasPendingBackupJobs() || [...clients].some((client) => client.hasPendingRequests());
}
export function hasPendingLocalWrites() {
  return [...clients].some((client) => client.hasPendingWrites());
}

export class LocalStoreClient {
  private readonly worker = new Worker(new URL("./local.worker.ts", import.meta.url), { type: "module" });
  private nextId = 1;
  private readonly pending = new Map<
    number,
    {
      resolve: (value: unknown) => void;
      reject: (error: Error) => void;
      workspaceId?: string;
      mutation: boolean;
      explicitEpochChange: boolean;
    }
  >();
  private failure?: Error;
  private syncClocks = new Map<string, SyncClock>();
  private dataEpochs = new Map<string, string>();
  private readonly reviewId = syncReviewId();
  private readonly syncSessionId = this.reviewId ?? (DRIVE_SYNC_AVAILABLE ? "app" : undefined);
  private unlockedScope?: string;

  constructor(private readonly account?: LocalAccount) {
    clients.add(this);
    this.worker.onmessage = (event: MessageEvent<LocalResponse>) => {
      const response = event.data;
      const request = this.pending.get(response.id);
      if (!request) return;
      this.pending.delete(response.id);
      if (response.ok) {
        if (
          response.dataEpoch !== undefined &&
          request.workspaceId &&
          (!this.dataEpochs.has(request.workspaceId) || request.explicitEpochChange)
        )
          this.dataEpochs.set(request.workspaceId, response.dataEpoch);
        if (response.syncState && request.workspaceId) {
          const previous = this.syncClocks.get(request.workspaceId);
          this.syncClocks.set(request.workspaceId, response.syncState.clock);
          if (
            request.mutation &&
            JSON.stringify(previous) !== JSON.stringify(response.syncState.clock) &&
            typeof window !== "undefined"
          )
            window.dispatchEvent(
              new CustomEvent("classroom-sync-v2-saved", { detail: { workspaceId: request.workspaceId } }),
            );
        }
        request.resolve(response.result);
      } else request.reject(new Error(response.error));
    };
    this.worker.onerror = (event) => {
      this.failure = new Error(event.message || "SQLite Worker 發生錯誤。");
      for (const request of this.pending.values()) request.reject(this.failure);
      this.pending.clear();
    };
  }

  hasPendingRequests() {
    return this.pending.size > 0;
  }
  hasPendingWrites() {
    return [...this.pending.values()].some((request) => request.mutation);
  }

  private async send<T>(operation: LocalOperation): Promise<T> {
    if (this.failure) return Promise.reject(this.failure);
    try {
      if (this.account) assertAccountSession(this.account);
      assertOperationScope(operation, this.account);
    } catch (error) {
      return Promise.reject(error);
    }
    const workspaceId =
      "workspaceId" in operation
        ? operation.workspaceId
        : "workspace" in operation
          ? operation.workspace.id
          : this.account?.workspaceId;
    // Capture before any asynchronous key/account handshake. An old queued
    // request cannot acquire the new restore epoch while it waits.
    const dataEpoch = workspaceId ? this.dataEpochs.get(workspaceId) : undefined;
    const sessionKey = this.account && this.syncSessionId ? `${this.account.workspaceId}:${this.syncSessionId}` : null;
    const syncSession = sessionKey ? syncSessions.get(sessionKey) : undefined;
    if (
      syncSession &&
      !operation.kind.startsWith("syncV2") &&
      !["accountStatus", "claimLegacyWorkspace", "deferLegacyWorkspace"].includes(operation.kind)
    ) {
      const scopeId = JSON.stringify(syncSession.scope);
      if (this.unlockedScope !== scopeId) {
        await this.send<void>({
          kind: "syncV2Unlock",
          workspaceId: this.account!.workspaceId,
          scope: syncSession.scope,
          key: syncSession.key,
        });
        this.unlockedScope = scopeId;
      }
    }
    const epoch = this.account
      ? operation.kind === "accountStatus"
        ? await beginAccountSession(this.account)
        : accountEpoch(this.account)
      : undefined;
    if (this.failure) throw this.failure;
    const id = this.nextId++;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, {
        resolve: resolve as (value: unknown) => void,
        reject,
        workspaceId,
        mutation: ["classroom", "restore"].includes(localOperationAccess[operation.kind]),
        explicitEpochChange: [
          "restoreAutomaticBackup",
          "replaceSnapshot",
          "importCloudExport",
          "syncV2CreateLocal",
          "syncV2Attach",
          "syncV2Join",
          "syncV2Generation",
          "syncV2ImportLegacy",
          "syncV2RemoteGeneration",
          "syncV2VerifyJoinBackup",
        ].includes(operation.kind),
      });
      try {
        const observedSyncClock = workspaceId ? this.syncClocks.get(workspaceId) : undefined;
        this.worker.postMessage({
          id,
          operation,
          account: this.account,
          accountEpoch: epoch,
          reviewId: this.reviewId,
          observedSyncClock,
          dataEpoch,
        } satisfies LocalRequest);
      } catch (error) {
        this.pending.delete(id);
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    });
  }

  syncState(workspaceId: string) {
    return this.send<LocalSyncState | null>({ kind: "syncV2State", workspaceId });
  }
  syncSeed(workspaceId: string) {
    return this.send<void>({ kind: "syncV2Seed", workspaceId });
  }
  async syncCreateLocal(workspaceId: string, scope: SyncAttachment, key: CryptoKey) {
    await this.send<void>({ kind: "syncV2CreateLocal", workspaceId, scope, key });
    if (this.syncSessionId) syncSessions.set(`${workspaceId}:${this.syncSessionId}`, { scope, key });
    this.unlockedScope = JSON.stringify(scope);
  }
  syncStats(workspaceId: string) {
    return this.send<import("./syncV2Store").SyncStats>({ kind: "syncV2Stats", workspaceId });
  }
  syncResolve(workspaceId: string, decision: ConflictV2, operationId: string) {
    return this.send<void>({ kind: "syncV2Resolve", workspaceId, decision, operationId });
  }
  syncConflictLabel(workspaceId: string, key: string) {
    return this.send<{ title: string }>({
      kind: "syncV2ConflictLabel",
      workspaceId,
      key,
    });
  }
  syncCheckpoint<T = void>(workspaceId: string, command: import("./syncCheckpointStore").CheckpointCommand) {
    return this.send<T>({ kind: "syncV2Checkpoint", workspaceId, command });
  }
  syncStage(workspaceId: string, operations: OperationV2[]) {
    return this.send<void>({ kind: "syncV2Stage", workspaceId, operations });
  }
  syncDrain(workspaceId: string, final: boolean) {
    return this.send<string[]>({ kind: "syncV2Drain", workspaceId, final });
  }
  syncVerifyJoinBackup(workspaceId: string, snapshot: LocalSnapshot) {
    return this.send<string>({ kind: "syncV2VerifyJoinBackup", workspaceId, snapshot });
  }
  async syncRekey(workspaceId: string, scope: SyncAttachment, key: CryptoKey) {
    await this.send<void>({ kind: "syncV2Rekey", workspaceId, scope, key });
    if (this.syncSessionId) syncSessions.set(`${workspaceId}:${this.syncSessionId}`, { scope, key });
    this.unlockedScope = JSON.stringify(scope);
  }
  async syncGeneration(workspaceId: string, scope: SyncAttachment, key: CryptoKey, receipt: string) {
    await this.send<void>({ kind: "syncV2Generation", workspaceId, scope, key, receipt });
    if (this.syncSessionId) syncSessions.set(`${workspaceId}:${this.syncSessionId}`, { scope, key });
    this.unlockedScope = JSON.stringify(scope);
  }
  async syncJoin(workspaceId: string, scope: SyncAttachment, key: CryptoKey, receipt?: string) {
    await this.send<void>({ kind: "syncV2Join", workspaceId, scope, key, receipt });
    if (this.syncSessionId) syncSessions.set(`${workspaceId}:${this.syncSessionId}`, { scope, key });
    this.unlockedScope = JSON.stringify(scope);
  }
  syncDisconnect(workspaceId: string) {
    return this.send<void>({ kind: "syncV2Disconnect", workspaceId });
  }
  syncAttach(workspaceId: string, scope: SyncAttachment) {
    return this.send<void>({ kind: "syncV2Attach", workspaceId, scope });
  }
  async syncUnlock(workspaceId: string, scope: SyncAttachment, key: CryptoKey) {
    await this.send<void>({ kind: "syncV2Unlock", workspaceId, scope, key });
    if (this.syncSessionId) syncSessions.set(`${workspaceId}:${this.syncSessionId}`, { scope, key });
    this.unlockedScope = JSON.stringify(scope);
  }
  clearSyncSession() {
    if (this.account && this.syncSessionId) syncSessions.delete(`${this.account.workspaceId}:${this.syncSessionId}`);
    this.unlockedScope = undefined;
  }
  syncOnline(workspaceId: string, expiresAt: number, expected: Pick<SyncAttachment, "datasetId" | "generation">) {
    return this.send<void>({ kind: "syncV2Online", workspaceId, expiresAt, expected });
  }
  syncDeny(workspaceId: string) {
    return this.send<void>({ kind: "syncV2Deny", workspaceId });
  }
  syncPending(workspaceId: string) {
    return this.send<OperationV2[]>({ kind: "syncV2Pending", workspaceId });
  }
  syncOperation(workspaceId: string, operationId: string) {
    return this.send<{ operation: OperationV2; fileId: string | null; status: string } | null>({
      kind: "syncV2Operation",
      workspaceId,
      operationId,
    });
  }
  syncAllocate(workspaceId: string, operationId: string, fileId: string) {
    return this.send<void>({ kind: "syncV2Allocate", workspaceId, operationId, fileId });
  }
  syncAcknowledge(workspaceId: string, operation: OperationV2, fileId: string) {
    return this.send<void>({ kind: "syncV2Acknowledge", workspaceId, operation, fileId });
  }
  syncReceive(workspaceId: string, operations: OperationV2[]) {
    return this.send<SyncReceiveResult>({ kind: "syncV2Receive", workspaceId, operations });
  }
  syncCursor(workspaceId: string, cursor: string, expected: Pick<SyncAttachment, "datasetId" | "generation">) {
    return this.send<void>({ kind: "syncV2Cursor", workspaceId, cursor, expected });
  }
  syncConflicts(workspaceId: string) {
    return this.send<ConflictV2[]>({ kind: "syncV2Conflicts", workspaceId });
  }

  previewCloudImport(workspaceId: string, text: string) {
    return this.send<CloudImportPreview>({ kind: "previewCloudImport", workspaceId, text });
  }
  accountStatus() {
    return this.send<"pending" | "none">({ kind: "accountStatus" });
  }
  claimLegacyWorkspace(email: string) {
    return this.send<void>({ kind: "claimLegacyWorkspace", email });
  }
  deferLegacyWorkspace() {
    return this.send<void>({ kind: "deferLegacyWorkspace" });
  }
  importCloudExport(workspaceId: string, text: string, checksum: string, targetChecksum: string) {
    return this.send<void>({ kind: "importCloudExport", workspaceId, text, checksum, targetChecksum });
  }
  ensureWorkspace(workspace: StoredRecord, hiddenTools: string[] = []) {
    return this.send<void>({ kind: "ensureWorkspace", workspace, hiddenTools });
  }
  saveClassSetup(workspaceId: string, draft: ClassSetupDraft, yearId?: string) {
    return this.send<void>({ kind: "saveClassSetup", workspaceId, draft, yearId });
  }
  importStudents(workspaceId: string, classId: string, rows: import("@classroom/domain").RosterRow[]) {
    return this.send<void>({ kind: "importStudents", workspaceId, classId, rows });
  }

  addStudent(workspaceId: string, classId: string, row: import("@classroom/domain").RosterRow) {
    return this.send<import("@classroom/domain").Student>({ kind: "addStudent", workspaceId, classId, row });
  }
  saveAcademicTerms(workspaceId: string, terms: AcademicTerm[], yearId?: string) {
    return this.send<void>({ kind: "saveAcademicTerms", workspaceId, terms, yearId });
  }
  loadYear(workspaceId: string, yearId?: string, date?: string) {
    return this.send<LocalSnapshot>({
      kind: "loadYear",
      workspaceId,
      yearId,
      date,
    });
  }
  ensureDateSchoolYear(workspaceId: string, date: string) {
    return this.send<void>({ kind: "ensureDateSchoolYear", workspaceId, date });
  }
  saveSchoolYearAction(workspaceId: string, action: import("@classroom/domain").SchoolYearAction) {
    return this.send<void>({ kind: "saveSchoolYearAction", workspaceId, action });
  }
  previewSchoolYearDeletion(workspaceId: string, yearId: string) {
    return this.send<ReturnType<import("./repository").LocalRepository["previewSchoolYearDeletion"]>>({
      kind: "previewSchoolYearDeletion",
      workspaceId,
      yearId,
    });
  }
  saveWorkspace(workspace: StoredRecord) {
    return this.send<void>({ kind: "saveWorkspace", workspace });
  }
  saveHiddenTools(workspaceId: string, hiddenTools: string[]) {
    return this.send<void>({ kind: "saveHiddenTools", workspaceId, hiddenTools });
  }
  load(workspaceId: string) {
    return this.send<LocalSnapshot>({ kind: "load", workspaceId });
  }
  exportBackupSource(workspaceId: string) {
    return this.send<LocalBackupSource>({ kind: "exportBackupSource", workspaceId });
  }
  backupProgress(workspaceId: string) {
    return this.send<import("@classroom/domain").BackupProgress>({ kind: "backupProgress", workspaceId });
  }
  captureAutomaticBackup(workspaceId: string) {
    return this.send<import("./repository").AutomaticBackupSource>({ kind: "captureAutomaticBackup", workspaceId });
  }
  acknowledgeAutomaticBackup(workspaceId: string, revision: number | null, now: number, restoreId?: string) {
    return this.send<void>({ kind: "acknowledgeAutomaticBackup", workspaceId, revision, now, restoreId });
  }
  restoreAutomaticBackup(workspaceId: string, snapshot: LocalSnapshot, expectedHash: string, restoreId?: string) {
    return this.send<void>({ kind: "restoreAutomaticBackup", workspaceId, snapshot, expectedHash, restoreId });
  }
  validateSnapshot(workspaceId: string, snapshot: LocalSnapshot) {
    return this.send<void>({ kind: "validateSnapshot", workspaceId, snapshot });
  }
  syncImportLegacy(workspaceId: string, snapshot: LocalSnapshot) {
    return this.send<void>({ kind: "syncV2ImportLegacy", workspaceId, snapshot });
  }
  syncMaintenance(workspaceId: string, value: "drive-rebind" | null) {
    return this.send<void>({ kind: "syncV2Maintenance", workspaceId, value });
  }
  syncResume(workspaceId: string) {
    return this.send<void>({ kind: "syncV2Resume", workspaceId });
  }
  syncRemoteGeneration(workspaceId: string, expected: SyncAttachment) {
    return this.send<void>({ kind: "syncV2RemoteGeneration", workspaceId, expected });
  }
  validateBackupImage(workspaceId: string, snapshot: LocalSnapshot, sqliteImage: string, schemaVersion: number) {
    return this.send<void>({ kind: "validateBackupImage", workspaceId, snapshot, sqliteImage, schemaVersion });
  }
  get(workspaceId: string, collection: Collection, recordId: string) {
    return this.send<StoredRecord | null>({ kind: "get", workspaceId, collection, recordId });
  }
  put(workspaceId: string, collection: Collection, record: StoredRecord) {
    return this.send<void>({ kind: "put", workspaceId, collection, record });
  }
  putMany(workspaceId: string, collection: Collection, records: StoredRecord[]) {
    return this.send<void>({ kind: "putMany", workspaceId, collection, records });
  }
  putCollections(workspaceId: string, batches: Array<{ collection: Collection; records: StoredRecord[] }>) {
    return this.send<void>({ kind: "putCollections", workspaceId, batches });
  }
  saveSchoolCalendarChanges(workspaceId: string, changes: import("@classroom/domain").SchoolCalendarChange[]) {
    return this.send<void>({ kind: "saveSchoolCalendarChanges", workspaceId, changes });
  }
  remove(workspaceId: string, collection: Collection, recordId: string) {
    return this.send<void>({ kind: "remove", workspaceId, collection, recordId });
  }
  removeMany(workspaceId: string, collection: Collection, recordIds: string[]) {
    return this.send<void>({ kind: "removeMany", workspaceId, collection, recordIds });
  }
  counts(workspaceId: string) {
    return this.send<Record<Collection, number>>({ kind: "counts", workspaceId });
  }
  deleteEmptyClass(workspaceId: string, classId: string, confirmedName: string) {
    return this.send<void>({ kind: "deleteEmptyClass", workspaceId, classId, confirmedName });
  }
  deleteStudentData(workspaceId: string, studentId: string) {
    return this.send<{ ok: true; recordsChanged: number }>({ kind: "deleteStudentData", workspaceId, studentId });
  }

  deleteTeachingAssignment(workspaceId: string, classId: string, assignmentId: string, confirmationToken: string) {
    return this.send<void>({ kind: "deleteTeachingAssignment", workspaceId, classId, assignmentId, confirmationToken });
  }
  replaceSnapshot(workspaceId: string, snapshot: LocalSnapshot) {
    return this.send<void>({ kind: "replaceSnapshot", workspaceId, snapshot });
  }

  updateClassSettings(workspaceId: string, classId: string, update: ClassSettingsUpdate) {
    return this.send<void>({ kind: "updateClassSettings", workspaceId, classId, update });
  }
  saveAssignmentFolderAction(workspaceId: string, classId: string, action: AssignmentFolderAction) {
    return this.send<void>({ kind: "saveAssignmentFolderAction", workspaceId, classId, action });
  }

  saveHomeroomAffairChange(workspaceId: string, classId: string, date: string, change: HomeroomAffairChange) {
    return this.send<void>({ kind: "saveHomeroomAffairChange", workspaceId, classId, date, change });
  }

  copyTeachingAssignments(workspaceId: string, request: AssignmentCopyRequest) {
    return this.send<void>({ kind: "copyTeachingAssignments", workspaceId, request });
  }

  saveDailyMemoAction(workspaceId: string, classId: string, action: DailyMemoAction) {
    return this.send<void>({ kind: "saveDailyMemoAction", workspaceId, classId, action });
  }
  saveDailyRecordAction(workspaceId: string, classId: string, date: string, action: DailyRecordAction) {
    return this.send<void>({ kind: "saveDailyRecordAction", workspaceId, classId, date, action });
  }

  saveDrawAction(workspaceId: string, classId: string, action: DrawAction) {
    return this.send<DrawState>({ kind: "saveDrawAction", workspaceId, classId, action });
  }

  hasFailed(): boolean {
    return Boolean(this.failure);
  }

  close(): void {
    clients.delete(this);
    this.failure = new Error("本地資料庫連線已關閉。");
    this.worker.terminate();
    for (const request of this.pending.values()) request.reject(this.failure);
    this.pending.clear();
  }
}

export async function getLocalStorageStatus(): Promise<{
  opfsAvailable: boolean;
  persistent: boolean;
  usageBytes?: number;
  quotaBytes?: number;
}> {
  const storage = navigator.storage;
  const opfsAvailable = Boolean(storage && "getDirectory" in storage && self.crossOriginIsolated);
  const [persistent, estimate] = await Promise.all([
    storage?.persisted?.() ?? Promise.resolve(false),
    storage?.estimate?.() ?? Promise.resolve({}),
  ]);
  return { opfsAvailable, persistent, usageBytes: estimate.usage, quotaBytes: estimate.quota };
}

// Call only after an explicit user action; some browsers prompt for permission.
export async function requestPersistentStorage(): Promise<boolean> {
  return navigator.storage?.persist?.() ?? false;
}
